import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Package, Plus, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { FitsList, PartKindBadge, StockBadge } from '../components/part-badges';
import { PartFormModal } from '../components/PartFormModal';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  PageLoader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Th,
} from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatMoney } from '../lib/format';
import { useDebouncedValue, useUrlState } from '../lib/hooks';
import { modelLabel, useCatalog } from '../lib/parts';
import type { Page, Part, PartKind } from '../lib/types';

const PAGE_SIZE = 50;

export function PartsPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const catalog = useCatalog();
  const [state, setState] = useUrlState({ kind: '', search: '', modelId: '', stock: '', page: '1' });
  const [search, setSearch] = useState(state.search);
  const debouncedSearch = useDebouncedValue(search, 300);
  const [adding, setAdding] = useState(false);
  const page = Number(state.page) || 1;

  useEffect(() => {
    if (debouncedSearch !== state.search) setState({ search: debouncedSearch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const parts = useQuery({
    queryKey: ['parts', 'list', state],
    queryFn: () =>
      api.get<Page<Part>>('/parts', {
        kind: state.kind || undefined,
        search: state.search || undefined,
        modelId: state.modelId || undefined,
        stock: state.stock || undefined,
        page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  const selectedModel = useMemo(() => {
    for (const manufacturer of catalog.data ?? []) {
      const model = manufacturer.models.find((m) => m.id === state.modelId);
      if (model) return { ...model, manufacturer };
    }
    return null;
  }, [catalog.data, state.modelId]);

  const filtered = !!(state.kind || state.search || state.modelId || state.stock);

  return (
    <>
      <PageHeader
        title="Parts"
        description="Accessories you hand out to people and rooms, and spare parts for repairs."
        actions={
          can('TECHNICIAN') && (
            <Button variant="primary" icon={<Plus />} onClick={() => setAdding(true)}>
              Add part
            </Button>
          )
        }
      />

      <Tabs
        className="mb-4"
        value={state.kind}
        onChange={(kind) => setState({ kind })}
        tabs={[
          { value: '', label: 'All parts' },
          { value: 'ACCESSORY', label: 'Accessories' },
          { value: 'REPAIR_PART', label: 'Repair parts' },
        ]}
      />

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-3">
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, part number, or model" className="pl-8" />
          </div>
          <Select value={state.modelId} onChange={(e) => setState({ modelId: e.target.value })} className="w-full sm:w-64">
            <option value="">Fits any model</option>
            {catalog.data?.map((manufacturer) => (
              <optgroup key={manufacturer.id} label={manufacturer.name}>
                {manufacturer.models.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
          <Select value={state.stock} onChange={(e) => setState({ stock: e.target.value })} className="w-full sm:w-44">
            <option value="">All stock levels</option>
            <option value="in">In stock</option>
            <option value="low">Low stock</option>
            <option value="out">Out of stock</option>
          </Select>
          {selectedModel && (
            <span className="text-xs text-slate-500">
              Showing parts made for {modelLabel(selectedModel)}.{' '}
              <button type="button" onClick={() => setState({ modelId: '' })} className="inline-flex items-center gap-0.5 font-medium text-brand-700 hover:underline">
                <X className="size-3" /> Clear
              </button>
            </span>
          )}
        </div>

        {parts.isLoading ? (
          <PageLoader />
        ) : parts.error ? (
          <ErrorState error={parts.error} onRetry={() => parts.refetch()} />
        ) : parts.data!.items.length === 0 ? (
          <EmptyState
            icon={<Package />}
            title={filtered ? 'No parts match' : 'No parts yet'}
            description={
              filtered
                ? 'Try a different search or filter.'
                : 'Track keyboards, mice, and docks you hand out, and spare screens or batteries for repairs.'
            }
            action={
              can('TECHNICIAN') && (
                <Button icon={<Plus />} onClick={() => setAdding(true)}>
                  Add part
                </Button>
              )
            }
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Part</Th>
                  <Th>Type</Th>
                  <Th>Fits</Th>
                  <Th>Stock</Th>
                  <Th>Assigned out</Th>
                  <Th>Kept in</Th>
                  <Th className="text-right">Unit cost</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {parts.data!.items.map((part) => (
                  <tr key={part.id} onClick={() => navigate(`/parts/${part.id}`)} className="cursor-pointer hover:bg-slate-50/60">
                    <Td>
                      <Link to={`/parts/${part.id}`} onClick={(e) => e.stopPropagation()} className="font-medium text-slate-900 hover:text-brand-700">
                        {part.name}
                      </Link>
                      <p className="text-xs text-slate-500">
                        {[part.manufacturer?.name, part.partNumber].filter(Boolean).join(' · ') || ' '}
                      </p>
                    </Td>
                    <Td>
                      <PartKindBadge kind={part.kind} />
                    </Td>
                    <Td className="max-w-xs text-xs">
                      <FitsList models={part.models} />
                    </Td>
                    <Td>
                      <StockBadge part={part} />
                    </Td>
                    <Td className="text-slate-600">{part.assignedQuantity || '—'}</Td>
                    <Td className="text-xs text-slate-600">{part.room ? `${part.room.name} · ${part.building?.name}` : (part.building?.name ?? '—')}</Td>
                    <Td className="text-right text-slate-600">{formatMoney(part.unitCost)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={parts.data!.total} onPage={(next) => setState({ page: String(next) }, { resetPage: false })} />
          </>
        )}
      </Card>

      <PartFormModal
        open={adding}
        onClose={() => setAdding(false)}
        defaults={{
          kind: (state.kind as PartKind) || (state.modelId ? 'REPAIR_PART' : 'ACCESSORY'),
          compatibleModelIds: state.modelId ? [state.modelId] : [],
        }}
        onSaved={(saved) => navigate(`/parts/${saved.id}`)}
      />
    </>
  );
}
